/**
 * Store Pinia des événements de production (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IProductionSamplingEvent } from "../models/productionSamplingEvent";
import { IProductionSamplingEventFilter } from "../models/filters/productionSamplingEventFilter";
import ProductionSamplingEventService from "../services/ProductionSamplingEventService";
import { IProductionSamplingEventForm } from "../models/forms/productionSamplingEventForm";
import { IProductionSamplingEventApiResponse } from "../models/api/productionSamplingEventApiResponse";
import { IProductionApiResponse } from "../models/api/productionApiResponse";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { ISamplingEvent } from "../models/samplingEvent";
import { IProduction } from "../models/production";
import { useCenterStore } from "./center.store";
import { useAgentStore } from "./agent.store";
import { useBoars } from "./boars.store";
import { useProductionStore } from "./production.store";
import { useParameterStore } from "./parameter.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

export const useProductionSamplingEventStore = defineStore("productionSamplingEvents", () => {
    // State
    const datas = ref<IProductionSamplingEvent[]>([]);
    const current = ref<IProductionSamplingEvent>({} as IProductionSamplingEvent);
    const itemForm = ref<IProductionSamplingEventForm>({} as IProductionSamplingEventForm);
    const isLoading = ref<boolean>(false);

    // Getters
    /**
     * Retourne si au moins une production entre les deux dates
     */
    const wereProductionCreatedDuringTimespan = computed(() => (date1: Date, date2: Date): boolean => {
        return datas.value.some((b) =>
            new Date(b.production.dateProduction!).setHours(0, 0, 0, 0) >= new Date(date1).setHours(0, 0, 0, 0) &&
            new Date(b.production.dateProduction!).setHours(0, 0, 0, 0) <= new Date(date2).setHours(0, 0, 0, 0)
        );
    });

    /**
     * Retourne les événements de production entre les 2 dates
     */
    const getProductionDuringTimespan = computed(() => (date1: Date, date2: Date): IProductionSamplingEvent[] | undefined => {
        if (datas.value.length > 0) {
            return datas.value.filter((b: IProductionSamplingEvent) => {
                const bTimestamp = new Date(b.production.dateProduction!).setHours(0, 0, 0, 0);
                return bTimestamp >= date1.setHours(0, 0, 0, 0) && bTimestamp <= date2.setHours(0, 0, 0, 0);
            });
        }
        return undefined;
    });

    // Actions - Hydration
    /**
     * Hydrate une liste d'événements de production en batch (optimisé pour la performance)
     * - La production imbriquée contient uniquement des IDs côté API (pattern PPE)
     * - Charge boarsStore, centerStore, agentStore si nécessaires (en parallèle)
     * - Crée des Maps d'index pour des lookups O(1)
     * - samplingEvent est retourné complet par le backend (pas d'hydratation nécessaire)
     */
    async function hydrateProductionSamplingEvents(items: IProductionSamplingEventApiResponse[]): Promise<IProductionSamplingEvent[]> {
        if (items.length === 0) return [];

        // 1. Récupérer les stores dépendants
        const boarsStore = useBoars();
        const centerStore = useCenterStore();
        const agentStore = useAgentStore();
        const parameterStore = useParameterStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];
        if (boarsStore.datas.length === 0) {
            loadPromises.push(boarsStore.load({ page: 1, pageSize: 0 }));
        }
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (agentStore.datas.length === 0) {
            loadPromises.push(agentStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps pour des lookups O(1)
        let boarsMap = new Map(boarsStore.datas.map(b => [b.id, b]));

        // Si un verrat n'est pas dans le store (ex: réformé/transféré, chargé avec isPresent=true),
        // charger les réformés et reconstruire la map pour ne pas perdre leurs données.
        const missingAnimalId = items.find(i => i.production && !boarsMap.has(i.production.animalId))?.production?.animalId;
        if (missingAnimalId) {
            await boarsStore.loadReformed();
            boarsMap = new Map(boarsStore.datas.map(b => [b.id, b]));
        }

        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const agentMap = new Map(agentStore.datas.map(a => [a.id, a]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrater tous les éléments en une seule passe
        const models: IProductionSamplingEvent[] = [];
        for (const item of items) {
            const model = { ...item } as unknown as IProductionSamplingEvent;

            // Hydrater les références de la production imbriquée
            // (le backend retourne production.ToBusiness(allInclude=false) : IDs uniquement)

            if (item.production) {
                const production = { ...item.production } as unknown as IProduction;
                production.animal = boarsMap.get(item.production.animalId) ?? production.animal;
                production.center = centerMap.get(item.production.centerId) ?? production.center;
                production.agentDilutor = agentMap.get(item.production.agentDilutorId) ?? production.agentDilutor;
                production.agentSampler = agentMap.get(item.production.agentSamplerId) ?? production.agentSampler;
                production.technique = parameterMap.get(item.production.techniqueId) ?? production.technique;
                model.production = production;
            }

            models.push(model);
        }

        return models;
    }

    // Actions

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IProductionSamplingEventFilter) {
        isLoading.value = true;
        const result = await ProductionSamplingEventService.getAll<IProductionSamplingEventFilter, IProductionSamplingEventApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateProductionSamplingEvents(result.data as IProductionSamplingEventApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un événement de production par son identifiant
     */
    async function get(id: number) {
        const result = await ProductionSamplingEventService.getById<IProductionSamplingEventApiResponse>(id);

        if (!result.error) {
            const [hydratedItem] = await hydrateProductionSamplingEvents([result.data as IProductionSamplingEventApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedItem;

            current.value = hydratedItem;
            itemForm.value = toForm(hydratedItem);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IProductionSamplingEvent> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IProductionSamplingEvent);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IProductionSamplingEvent | IDataOperationResult<IProductionSamplingEvent>>;

        if (itemForm.value.id > 0) {
            result = await ProductionSamplingEventService.update(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateProductionSamplingEvents([result.data as unknown as IProductionSamplingEventApiResponse]);
                current.value = hydratedItem;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IProductionSamplingEvent>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await ProductionSamplingEventService.create(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateProductionSamplingEvents([result.data as unknown as IProductionSamplingEventApiResponse]);
                current.value = hydratedItem;
                // Ne pas pousser dans datas ici : le handler SignalR MajProductionSamplingEvent (Create)
                // s'en charge pour éviter un doublon (SignalR arrive avant la réponse HTTP).
            }
        }

        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await ProductionSamplingEventService.delete<IProductionSamplingEventApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem(undefined);
            itemForm.value = newForm(0);
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IProductionSamplingEvent): IProductionSamplingEventForm {
        const form = {} as IProductionSamplingEventForm;
        form.id = item.id;
        if (item.production != undefined)
            form.productionId = item.production?.id;
        form.samplingEventId = item.samplingEvent?.id;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number): IProductionSamplingEventForm {
        itemForm.value = {
            id: 0,
            productionId: lienId,
            samplingEventId: 0,
        } as IProductionSamplingEventForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien?: IProduction): IProductionSamplingEvent {
        current.value = {
            id: 0,
            production: lien,
            samplingEvent: { id: 0 } as ISamplingEvent
        } as IProductionSamplingEvent;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Getters
        wereProductionCreatedDuringTimespan,
        getProductionDuringTimespan,
        // Actions
        hydrateProductionSamplingEvents,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajProductionSamplingEvent", async (payload: IPayloadStore) => {
    const store = useProductionSamplingEventStore();
    const productionStore = useProductionStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateProductionSamplingEvents([payload.data as IProductionSamplingEventApiResponse]);
                store.datas.push(hydrated);

                // Sync l'array samplingEvents embarqué dans productionStore
                // (item.production?.samplingEvents dans HistoriqueProduction.vue)
                const production = productionStore.datas.find(p => p.id == payload.data?.productionId);
                if (production) {
                    if (!production.samplingEvents) production.samplingEvents = [];
                    if (production.samplingEvents.findIndex(se => se.id == hydrated.id) == -1)
                        production.samplingEvents.push(hydrated);
                }
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateProductionSamplingEvents([payload.data as IProductionSamplingEventApiResponse]);
                store.datas.splice(idx, 1, hydrated);

                // Sync l'array samplingEvents embarqué dans productionStore
                const production = productionStore.datas.find(p => p.id == payload.data?.productionId);
                if (production?.samplingEvents) {
                    const seIdx = production.samplingEvents.findIndex(se => se.id == hydrated.id);
                    if (seIdx > -1) production.samplingEvents[seIdx] = hydrated;
                }
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);

            // Sync l'array samplingEvents embarqué dans productionStore
            for (const production of productionStore.datas) {
                const seIdx = production.samplingEvents?.findIndex(se => se.id == payload.id) ?? -1;
                if (seIdx > -1) {
                    production.samplingEvents.splice(seIdx, 1);
                    break;
                }
            }
            break;
        }
    }
});
